import React from 'react'

import {
  ItemSelector,
  type ItemSelectorProps,
} from '~/components/elements/ItemSelector'
import { type SurveyGroupCustomerAttributeGroup as SurveyGroupCustomerAttributeGroupBase } from '~/gql/graphql'
import { type Unassigned, unassignedSymbol } from '~/utils'

type SurveyGroupCustomerAttributeGroup = App.PickGraphQL<
  SurveyGroupCustomerAttributeGroupBase,
  'id' | 'name'
>

export type SurveyGroupCustomerAttributeGroupSelectorProps<
  TSurveyGroupCustomerAttributeGroup extends SurveyGroupCustomerAttributeGroup,
> = Omit<
  ItemSelectorProps,
  'data' | 'dataItemKey' | 'onChange' | 'textField' | 'value'
> & {
  customerAttributeGroups: TSurveyGroupCustomerAttributeGroup[]
  onChange?: (
    customerAttributeGroupId: TSurveyGroupCustomerAttributeGroup['id'],
  ) => void
  value: TSurveyGroupCustomerAttributeGroup['id'] | Unassigned
}

export const SurveyGroupCustomerAttributeGroupSelector = React.forwardRef(
  <
    TSurveyGroupCustomerAttributeGroup extends
      SurveyGroupCustomerAttributeGroup,
  >(
    {
      customerAttributeGroups,
      onChange,
      value: valueProp,
      ...rest
    }: SurveyGroupCustomerAttributeGroupSelectorProps<TSurveyGroupCustomerAttributeGroup>,
    ref: React.ForwardedRef<HTMLSpanElement>,
  ) => {
    const value = React.useMemo(
      () =>
        customerAttributeGroups.find(({ id }) => id === valueProp) ?? {
          id: unassignedSymbol,
        },
      [customerAttributeGroups, valueProp],
    )

    const handleChange = React.useCallback<EH<ItemSelectorProps, 'onChange'>>(
      (customerAttributeGroup: TSurveyGroupCustomerAttributeGroup) => {
        onChange?.(customerAttributeGroup.id)
      },
      [onChange],
    )

    return (
      <ItemSelector
        ref={ref}
        data={customerAttributeGroups}
        dataItemKey="id"
        textField="name"
        value={value}
        onChange={handleChange}
        {...rest}
      />
    )
  },
)
